import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  Dialog,
  Select,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  DialogContent,
} from "@mui/material";

//Import from project
import "../../Style/dialogstyle.scss";
import { useMutation } from "@apollo/client";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import {
  CREATE_COMMUNE_SANGKAT,
  UPDATE_COMMUNE_SANGKAT,
} from "../../Schema/CapitalProvince";

export default function CommuneForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
  hideBackdrop,
  districtMunicipalitiesId
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const queryParams = new URLSearchParams(window.location.search);
  const provinceId = queryParams.get("provinceId");

  const [createCommuneSangkat] = useMutation(CREATE_COMMUNE_SANGKAT, {
    onCompleted: ({ createCommuneSangkat }) => {
      setLoading(false);
      if (createCommuneSangkat?.isSuccess === true) {
        setAlert(true, "success", createCommuneSangkat?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createCommuneSangkat?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateCommuneSangkat] = useMutation(UPDATE_COMMUNE_SANGKAT, {
    onCompleted: ({ updateCommuneSangkat }) => {
      setLoading(false);
      if (updateCommuneSangkat?.isSuccess === true) {
        setAlert(true, "success", updateCommuneSangkat?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateCommuneSangkat?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    commune_sangkat_type: Yup.string().required(t("required")),
    commune_sangkat_code: Yup.string().required(t("required")),
    commune_sangkat_name_en: Yup.string().required(t("required")),
    commune_sangkat_name_kh: Yup.string().required(t("required")),
  });

  const formik = useFormik({
    initialValues: {
      commune_sangkat_type: "commune",
      commune_sangkat_code: "",
      commune_sangkat_name_en: "",
      commune_sangkat_name_kh: "",
    },

    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      if (dialogTitle === "Create") {
        createCommuneSangkat({
          variables: {
            input: {
              ...values,
              capical_provinces_id: provinceId,
              district_municipalities_id: districtMunicipalitiesId,
            },
          },
        });
      } else {
        updateCommuneSangkat({
          variables: {
            id: editData?._id,
            input: {
              ...values,
              capical_provinces_id: provinceId,
              district_municipalities_id: districtMunicipalitiesId,
            },
          },
        });
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
  } = formik;

  useEffect(() => {
    if (editData) {
      setFieldValue("commune_sangkat_type", editData?.commune_sangkat_type);
      setFieldValue("commune_sangkat_code", editData?.commune_sangkat_code);
      setFieldValue(
        "commune_sangkat_name_en",
        editData?.commune_sangkat_name_en
      );
      setFieldValue(
        "commune_sangkat_name_kh",
        editData?.commune_sangkat_name_kh
      );
    }
  }, [editData, open]);

  return (
    <Dialog
      fullWidth
      open={open}
      maxWidth="xs"
      className="dialog-container"
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_commune_sangkat")
            : t("modal_title_update_commune_sangkat")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_code")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_code")}
                  {...getFieldProps("commune_sangkat_code")}
                  error={Boolean(
                    touched.commune_sangkat_code &&
                    errors.commune_sangkat_code
                  )}
                  helperText={
                    touched.commune_sangkat_code &&
                    errors.commune_sangkat_code
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_type")}
                </Typography>
                <FormControl fullWidth className="search-field" size="small">
                  <Select {...getFieldProps("commune_sangkat_type")}>
                    <MenuItem value={"commune"}>
                      {t("thead_commune")}
                    </MenuItem>
                    <MenuItem value={"sangkat"}>{t("thead_sangkat")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_name")}(KH)
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_name")}
                  {...getFieldProps("commune_sangkat_name_kh")}
                  error={Boolean(
                    touched.commune_sangkat_name_kh &&
                    errors.commune_sangkat_name_kh
                  )}
                  helperText={
                    touched.commune_sangkat_name_kh &&
                    errors.commune_sangkat_name_kh
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_name")}(ENG)
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_name")}
                  {...getFieldProps("commune_sangkat_name_en")}
                  error={Boolean(
                    touched.commune_sangkat_name_en &&
                    errors.commune_sangkat_name_en
                  )}
                  helperText={
                    touched.commune_sangkat_name_en &&
                    errors.commune_sangkat_name_en
                  }
                />
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    className={t("class-btn-create")}
                    fullWidth
                    onClick={handleSubmit}
                  >
                    {dialogTitle === "Create" ? t("create") : t("update")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
